//done
//@ts-check
import Alpine from "alpinejs";
import * as config from "../constants.js";
import { displayCharacter } from "./character.js";
import { gameState, saveGameState } from "./gameState.js";
import { liveCharacter, canAccessRaid } from "./gameState-core.js";
import { addNotification } from "./notifications.js";
import { multiplayer } from "./multiplayer.js";
import { updateRaidButtonStates } from "./raid.js";
import { refreshToolbarValues, renderLayerToolbar } from "./layerNav.js";
import { isCommandRevealed, availableRegionsForCharacter } from "./command-core.js";
import { getSkillCheckChance } from "./eventSystem-core.js";
import { getSkillArtifactBonus, getUtilityArtifactBonus, gainExperienceCore } from "./character-core.js";
import { getTechniqueSkill, getSkillDisplayName } from "./util.js";
import { debug, isDebug, CH } from './debugLog.js';
import { renderBlackoutStrip } from "./blackout.js";
import { regionSkillFit, FIT_DISPLAY } from "./regionFit-core.js";
import { getRegionPhase, getTilePhase } from "./worldClock-core.js";

// Update header background based on selected faction
export function updateHeaderBackground(faction = 'dominion') {
  const header = document.getElementById("game-header");
  if (!header) return;

  // Remove existing faction classes
  header.classList.remove("header-bg-dominion", "header-bg-veil", "header-bg-wilders");

  // Add appropriate faction class
  if (faction) {
    header.classList.add(`header-bg-${faction}`);
  }
  else {
    header.classList.add(`header-bg-dominion`);
  }
}

// Check if current character can access raid interface
// Moved to gameState-core so the layer registry can gate Field's active task on
// it. Pure gameState logic that happened to live in a DOM-coupled module.
export { canAccessRaid };

/**
 * Re-render the toolbar after a change to what should be reachable.
 *
 * This used to toggle `hidden`/`disabled` classes on the header's tab buttons.
 * That approach cannot survive a re-rendering toolbar — the next
 * renderLayerToolbar() rebuilt the buttons without the classes, so with no
 * character the screen tabs reappeared despite this having hidden them.
 *
 * All three rules it enforced are now declarative in the layer registry:
 *   - no character  -> Field's screens have visible: () => !!gameState.character
 *   - Command reveal -> the Command layer's unlocked()
 *   - raid ownership -> Field's activeTask returns 'none' unless canAccessRaid()
 *
 * Kept as a named call because it marks the moments those inputs change, and
 * the callers read better for it.
 */
export function updateTabVisibility() {
  renderLayerToolbar();
}

// Update UI
export function updateUI() {
  // The raid button's label/state used to be computed here from raid state.
  // That logic now lives in the layer registry as Field's activeTask (the same
  // reads, same order), and the toolbar renders it — raiding is Field's task,
  // not a privileged global control. See layers-core.js / layerNav.js.
  //
  // Values, NOT a structural rebuild: updateUI() is on the per-second interval,
  // and forcing a rebuild each tick both wasted the render split and closed the
  // layer panel a second after opening it. refreshToolbarValues() promotes
  // itself to a rebuild only when the task state actually changes.
  refreshToolbarValues();

  // Update raid button states when UI updates
  updateRaidButtonStates();
}

// Update faction availability based on existing characters
export function updateFactionAvailability() {
  const usedFactions = new Set();

  // Get factions of all existing characters
  debug(CH.ui, "Existing characters:", gameState.characters);
  if (gameState.characters && gameState.characters.length > 0) {
    gameState.characters.forEach((character) => {
      usedFactions.add(character.faction);
    });
  }
  debug(CH.ui, "Used factions:", usedFactions);

  // Update faction cards
  document.querySelectorAll(".faction-card").forEach((card) => {
    const faction = card.dataset.faction;
    if (usedFactions.has(faction)) {
      // Disable this faction
      card.classList.add("faction-disabled", "cursor-not-allowed");
      card.classList.remove("cursor-pointer");

      // Add visual indicator
      if (!card.querySelector(".faction-used-indicator")) {
        const indicator = document.createElement("div");
        indicator.className = "faction-used-indicator text-red-400 text-sm font-bold mt-2";
        indicator.textContent = "✓ Already Created";
        card.appendChild(indicator);
      }
    } else {
      // Enable this faction
      card.classList.remove("faction-disabled", "cursor-not-allowed");
      card.classList.add("cursor-pointer");

      // Remove visual indicator
      const indicator = card.querySelector(".faction-used-indicator");
      if (indicator) {
        indicator.remove();
      }
    }
  });
}

// Region and Tier selection
/**
 * Refresh the raid planner for the current region/tier.
 *
 * This used to render every skill in the region as a three-column wall of
 * level bands, ticks and success percentages. It was accurate and far too tall:
 * on a 768px viewport it pushed the region tiles and the Generate button below
 * the fold. The same information is still available where it is actually
 * actionable — once the grid exists and the player is choosing a path, each
 * room states its own skill and target — so the planner now answers only the
 * question it is for: can I handle this place? One glyph per tile
 * (regionFit-core.js), and a wrong guess costs one Cancel.
 */
export function updateRaidSkillsInfo() {
  const regionInfo = document.getElementById("region-info");
  const generateBtn = document.getElementById("generate-grid");

  // The Blackout strip rides this same refresh: its cards are gated by TIER,
  // so it has to repaint whenever the tier changes — and this is the one
  // function every region/tier change already funnels through.
  renderBlackoutStrip();
  // Fit is per region AND tier, so every tile restamps when the tier changes.
  refreshRegionFitBadges();

  const regionKey = gameState.currentRaid.selectedRegion;
  const tier = gameState.currentRaid.selectedTier;

  if (regionKey == null || tier == null) {
    regionInfo.innerHTML = "Select a region and tier to see details...";
    generateBtn.disabled = true;
    return;
  }

  const region = config.raidRegions[regionKey];
  const fit = regionSkillFit(regionKey, tier);
  const display = FIT_DISPLAY[fit?.verdict || 'unknown'];
  const factionName = config.factionSkills?.[gameState.character?.faction]?.name || "your faction";

  // The two inputs, stated separately. The composite is a ranking rather than a
  // probability (see regionFit-core), so printing it as a percentage would be a
  // lie — and the halves are what a player can act on anyway: train a skill, or
  // raid somewhere that uses more of them.
  const weakestName = fit?.weakest ? getSkillDisplayName(fit.weakest.skillId) : null;
  regionInfo.innerHTML = `
        <div class="text-sm flex flex-wrap items-baseline gap-x-2">
            <span class="font-bold text-white">${region.icon || "🗺️"} ${region.name}</span>
            <span class="text-gray-400">${config.raidTierData[tier].name}</span>
            ${fit ? `<span class="font-bold" style="color:${display.color}">${display.glyph} ${display.label}</span>
                     <span class="text-gray-400">${factionName} covers ${Math.round(fit.coverage * 100)}% of checks · you pass ${Math.round(fit.levelFit * 100)}% of those${
                        weakestName ? ` · weakest: ${weakestName} ${Math.round(fit.weakest.chance * 100)}%` : ''}</span>`
                   : '<span class="text-gray-400">Create a character to see your fit</span>'}
        </div>`;

  generateBtn.disabled = !gameState.character;
  saveGameState();
}

/**
 * Stamp each region tile with its fit glyph.
 *
 * Separate from tile CREATION because fit depends on tier, which changes far
 * more often than the tile list does — rebuilding every tile (and its
 * background image) to restate one character would be a visible flicker.
 */
export function refreshRegionFitBadges() {
  const tier = gameState.currentRaid?.selectedTier;
  document.querySelectorAll("#region-tiles .region-tile").forEach((tile) => {
    const badge = tile.querySelector(".region-fit-badge");
    if (!badge) return;
    // A locked region's fit is not a fact the player can act on — the lock
    // overlay is the whole answer, and a second verdict beside it just competes
    // with it.
    if (tile.dataset.locked === "true") { badge.textContent = ""; badge.title = ""; return; }
    const fit = tier ? regionSkillFit(tile.dataset.region, tier) : null;
    const display = FIT_DISPLAY[fit?.verdict || 'unknown'];
    badge.textContent = display.glyph;
    badge.title = fit
      ? `${display.label} — your faction covers ${Math.round(fit.coverage * 100)}% of this region's checks, and you pass ${Math.round(fit.levelFit * 100)}% of those at this tier`
      : "Pick a tier to see how well your skills fit";
  });
}

// Where a region tile's clock is being read: the capital, or the tile a raid
// has actually been sited on.
const CAPITAL_ICON = '🏛️';
const SITE_ICON = '📍';

/**
 * Stamp each region tile with a LOCAL time (M3 S9).
 *
 * Same split as the fit badge — build the tiles once, restate the volatile bit
 * on its own cadence — except the trigger here is the clock rather than a tier
 * click, so this is driven from the 5s tick in main.js. A game day is 10 real
 * minutes, so 5s moves the world by about 12 minutes: fast enough that a player
 * comparing regions sees them diverge, slow enough to cost nothing.
 *
 * WHICH clock depends on whether there is a raid sited here. Normally the badge
 * reads the region's CAPITAL — a region three or four columns wide spans four
 * to six hours, so there is no one time for it, and the capital is the place
 * the rest of the game already treats as the region's point. But once a grid is
 * generated the raid has a real site, and that site's clock is the one the
 * player is about to see over the map. Showing the capital's instead would have
 * the number change for no visible reason on the way in.
 *
 * A LOCKED region still shows its clock. Unlike skill fit, time of day is not a
 * verdict on the player's readiness — it is a fact about the world, and the
 * world turns whether or not you hold the charter.
 */
export function refreshRegionClocks() {
  const now = Date.now();
  // The site belongs to the GRID — cancelling leaves the field behind (two
  // teardown paths null the grid and neither owns this rule), so the grid is
  // what says whether a site is live. Checking here rather than clearing there
  // is one condition instead of one per teardown path.
  const { selectedRegion, raidSite, currentRaidGrid } = gameState.currentRaid || {};
  const liveSite = currentRaidGrid ? raidSite : null;
  document.querySelectorAll("#region-tiles .region-tile").forEach((tile) => {
    const badge = tile.querySelector(".region-clock-badge");
    if (!badge) return;
    const regionId = tile.dataset.region;
    const sited = liveSite && regionId === selectedRegion;
    const local = sited ? getTilePhase(liveSite, now) : getRegionPhase(regionId, now);
    // WHERE the clock is read, before WHAT it reads. Without the place marker
    // the badge silently changes meaning when a raid is sited here — same
    // format, different location — and the time appearing to jump for no
    // reason is exactly what the marker prevents.
    badge.textContent = `${sited ? SITE_ICON : CAPITAL_ICON} ${local.icon} ${local.time}`;
    badge.title = sited
      ? `Local time at your raid site — ${local.name}.`
      : `Local time at the capital — ${local.name}. A region spans several hours of longitude, so a raid sited within it can meet a different sun.`;
  });
}

// Generate tier slider dynamically
export function generateTierSlider() {
  const sliderContainer = document.getElementById("tier-slider");
  sliderContainer.innerHTML = "";

  Object.keys(config.raidTierData).forEach((tierKey) => {
    const tier = parseInt(tierKey);
    const data = config.raidTierData[tier];

    // Extract short name and description from "Tier X - Name" format
    const shortName = data.name.split(" - ")[0] || tier;
    const shortDescription = data.name.split(" - ")[1] || data.name;

    const segment = document.createElement("div");
    segment.className =
      "tier-segment flex-1 cursor-pointer rounded-lg p-1 text-center font-bold transition-all border-2 border-gray-500 bg-gradient-to-b from-gray-700 to-gray-800 hover:border-gray-400 text-gray-400";
    segment.dataset.tier = tier;
    segment.innerHTML = `
            <div class="text-sm">${shortName}</div>
            <div class="text-xs">${shortDescription} - ${data.gridRows} x ${data.gridCols} Grid</div>
        `;

    // Check if this should be selected initially
    if (gameState.currentRaid.selectedTier === tier) {
      segment.classList.add("selected");
    }

    segment.addEventListener("click", () => {
      document.querySelectorAll(".tier-segment").forEach((s) => s.classList.remove("selected"));
      segment.classList.add("selected");
      gameState.currentRaid.selectedTier = tier;
      updateRaidSkillsInfo();
    });

    sliderContainer.appendChild(segment);
  });
}

// Generate region tiles dynamically
export function generateRegionTiles() {
  const tilesContainer = document.getElementById("region-tiles");
  tilesContainer.innerHTML = "";

  // Region locks (Command layer): faction starting pair + chartered regions.
  // Grandfathered pre-Command saves see everything.
  const availableRegions = new Set(availableRegionsForCharacter(gameState.character));

  // A locked region can't stay selected (e.g. a fresh character after promotion).
  //
  // ...but only while it's just a menu choice. Once a raid is set up or running,
  // selectedRegion is live state the grid, raid header and room rendering all
  // resolve through (`config.raidRegions[selectedRegion]`), so clearing it breaks
  // the raid in place. That happened on reload — boot calls this unconditionally,
  // unlike the raid-tab handler which skips it during a raid — and on switching
  // to a character whose faction doesn't share the region. The lock's job is to
  // stop you *starting* a raid somewhere locked, not to retroactively break one.
  const raidUsingRegion = gameState.currentRaid.currentRaidGrid
    || gameState.currentRaid.raidInProgress
    || gameState.currentRaid.raidCompleted;

  if (!raidUsingRegion && gameState.currentRaid.selectedRegion && !availableRegions.has(gameState.currentRaid.selectedRegion)) {
    gameState.currentRaid.selectedRegion = null;
  }

  Object.keys(config.raidRegions).forEach((regionKey) => {
    const region = config.raidRegions[regionKey];
    const icon = region.icon || "🗺️";
    const bgImage = `./images/regions/${regionKey}/RegionBackground.jpg`;
    const locked = !availableRegions.has(regionKey);

    const tile = document.createElement("div");
    tile.className = "region-tile overflow-hidden rounded-lg border-2 transition-all " + (locked
      ? "relative border-gray-700 cursor-not-allowed"
      : "cursor-pointer border-gray-600 hover:border-game-accent");
    tile.dataset.region = regionKey;
    tile.dataset.locked = String(locked);
    tile.style.backgroundImage = `url('${bgImage}')`;
    tile.style.backgroundSize = "135% auto";
    tile.style.backgroundPosition = "center";
    tile.style.transition = "background-position 1s ease-out, border-color 0.25s";
    if (locked) {
      tile.style.filter = "grayscale(0.9) brightness(0.7)";
    }
    tile.innerHTML = `
            <div class="p-4 bg-gradient-to-b from-transparent via-gray-900/40 to-gray-900/90 h-32 flex flex-col justify-end relative">
                <!-- Skill fit, stamped by refreshRegionFitBadges() rather than
                     rendered here: it depends on TIER, which changes far more
                     often than the tile list does. -->
                <div class="region-fit-badge absolute top-2 right-2 text-lg leading-none drop-shadow"></div>
                <!-- Local time (M3 S9). Stamped by refreshRegionClocks() on the
                     tick: this one moves on its own, without anybody clicking. -->
                <div class="region-clock-badge absolute top-2 left-2"></div>
                <div class="text-white font-bold">${icon} ${region.name}</div>
                <div class="text-xs text-gray-400">${region.theme}</div>
            </div>
            ${locked ? `
            <div class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 bg-black/45">
                <div class="text-2xl">🔒</div>
                <div class="text-xs px-2 py-0.5 rounded border" style="color:#b18cff; border-color:#6d4fa3; background:rgba(12,8,20,0.85);">Regional Charter</div>
            </div>` : ""}
        `;

    if (locked) {
      // Allocation and licensing, NOT physical reachability (StoryAlignment.md
      // decision 2). The tooltip is the load-bearing half: it is the only place
      // in L1/L2 that can explain why an Operation may cross into a locked
      // region while a raid may not — gates move people, ground moves tonnage.
      tile.title = "Reachable overland — but your faction holds no gate allocation or charter here.";
      tile.addEventListener("click", () => {
        addNotification("🔒 No gate allocation here — your faction's crossings land elsewhere. A Regional Charter rank (Command layer → Promotion) licenses a region of your choice", "error");
      });
      tilesContainer.appendChild(tile);
      return;
    }

    // Check if this should be selected initially
    if (gameState.currentRaid.selectedRegion === regionKey) {
      tile.classList.add("selected");
    }

    tile.addEventListener("click", () => {
      document.querySelectorAll(".region-tile").forEach((t) => t.classList.remove("selected"));
      tile.classList.add("selected");
      gameState.currentRaid.selectedRegion = regionKey;
      saveGameState();
      updateRaidSkillsInfo();
    });

    // Parallax effect on mouse move
    tile.addEventListener("mousemove", (e) => {
      const rect = tile.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      // Background at 180% gives room for movement
      // Invert X for parallax effect (mouse right = image left)
      const maxXOffset = 460 / 4;
      const maxYOffset = 130 / 4;

      const offsetX = 50 - (x - 0.5) * maxXOffset; // Inverted
      const offsetY = 50 + (y - 0.5) * maxYOffset;

      tile.style.backgroundPosition = `${offsetX}% ${offsetY}%`;
    });

    tile.addEventListener("mouseleave", () => {
      tile.style.backgroundPosition = "center";
    });

    tilesContainer.appendChild(tile);
  });

  // Tiles are freshly built and carry empty badges — stamp them once here so a
  // boot or character switch shows fit and local time without waiting for a
  // tier click or the next tick.
  refreshRegionFitBadges();
  refreshRegionClocks();
}

// Re-onboard characters that aren't yet owner-protected on the server. Two
// cases, both limited to trivial stakes (level <= 2, no artifacts/gear — matches
// the server's claim-legacy eligibility) so untrusted higher-level local stats
// never reach the DB:
//   1. Has a server row but no local token (pre-token / legacy) -> claim it,
//      upgrading the null-hash "grace" row to owner-protected.
//   2. No server row at all (initial registration failed, e.g. created offline)
//      -> register it now via hardened create, which mints the row + token.
// Run once on load; non-blocking.
export async function claimLegacyCharacters() {
    const chars = gameState.characters || [];
    let changed = false;

    for (const c of chars) {
        // Mutate the authoritative copy (see liveCharacter): writing only to the
        // roster entry of the active character is clobbered on the next save.
        // Mirror onto the roster entry so reads before that save agree too.
        const ch = liveCharacter(c);

        if (ch.multiplayerId && ch.ownerToken) continue; // already fully onboarded

        const eligible = (ch.level || 1) <= 2
            && (Array.isArray(ch.artifacts) ? ch.artifacts.length : 0) === 0
            && Object.keys(ch.equippedTechniqueGear || {}).length === 0;
        if (!eligible) continue;

        try {
            if (ch.multiplayerId) {
                // Case 1 — claim the existing pre-token row.
                const token = await multiplayer.claimLegacyCharacter(ch.multiplayerId);
                if (token) {
                    ch.ownerToken = token;
                    if (ch !== c) c.ownerToken = token;
                    changed = true;
                    console.log(`Re-onboarded legacy character ${ch.name}`);
                }
            } else {
                // Case 2 — never registered; create the server row now.
                const registered = await multiplayer.registerCharacter(ch);
                const newId = registered?.id || registered?.character?.id || null;
                if (newId) {
                    ch.multiplayerId = newId;
                    ch.ownerToken = registered.ownerToken || null;
                    if (ch !== c) {
                        c.multiplayerId = newId;
                        c.ownerToken = ch.ownerToken;
                    }
                    changed = true;
                    console.log(`Registered previously-unsynced character ${ch.name}`);
                }
            }
        } catch (err) {
            // Offline or name-taken, etc. — leave the character local-only.
            console.warn(`Could not re-onboard character ${ch.name}:`, err);
        }
    }

    if (changed) saveGameState();
}

// Check for and apply pending payments from database.
// Pending payments are read and marked per character with that character's own
// token (the server enforces recipient ownership), so we process one character
// at a time rather than in a single cross-character batch.
export async function checkAndApplyPendingPayments() {
    try {
        const chars = (gameState.characters || []).filter(c => c.multiplayerId);
        if (chars.length === 0) {
            return;
        }

        let totalCredits = 0;
        let totalXP = 0;

        for (const rosterEntry of chars) {
            // Write (and read artifacts) through the authoritative copy — for the
            // active character the roster entry is overwritten by
            // gameState.character on the next save, which silently discarded the
            // XP awarded below.
            const character = liveCharacter(rosterEntry);
            const pending = await multiplayer.getPendingPayments(rosterEntry.multiplayerId);
            if (!pending || pending.length === 0) continue;

            // Contract Beacon on the contracted-out character: +X% on receipt.
            // The hiring client can't see this character's artifacts and the
            // server stores amounts verbatim, so the owner's client applies the
            // bonus when collecting.
            const payoutBonus = 1 + getUtilityArtifactBonus('contractor-payout-bonus', character) / 100;
            let credits = 0;
            let xp = 0;
            const paymentIds = [];
            for (const payment of pending) {
                credits += Math.floor((payment.amount || 0) * payoutBonus);
                xp += payment.xp || 0;
                paymentIds.push(payment.id);
            }

            // Mark processed FIRST to prevent double-application if the client
            // crashes before applying.
            const marked = await multiplayer.markPaymentsProcessed(rosterEntry.multiplayerId, paymentIds);
            if (!marked) {
                console.error(`Failed to mark payments processed for ${character.name}; skipping to avoid duplicates`);
                continue;
            }

            gameState.player.credits += credits; // credits are account-global
            if (xp > 0) {
                gainExperienceCore(character, xp); // XP goes to the recipient character
            }
            totalCredits += credits;
            totalXP += xp;
        }

        if (totalCredits > 0 || totalXP > 0) {
            saveGameState();
            displayCharacter(); // refresh the active character's panel if it changed

            if (totalCredits > 0 && totalXP > 0) {
                addNotification(`💰 Received ${totalCredits} credits and ${totalXP} XP from contractor payments!`, 'success');
            } else if (totalCredits > 0) {
                addNotification(`💰 Received ${totalCredits} credits from contractor payments!`, 'success');
            } else if (totalXP > 0) {
                addNotification(`⭐ Received ${totalXP} XP from contractor payments!`, 'success');
            }
        }
    } catch (error) {
        console.error('Failed to check pending payments:', error);
    }
}

// Refresh each character's marketplace state from the server. marketplaceData is
// cached locally when a character is listed, so the client would otherwise never
// learn about a server-side change to it — a listing withdrawn from another
// device, or the dev time-shift tool (tools/dev/contract-dev.mjs) moving the
// clock. Run on load BEFORE the expiry check so that check reasons about server
// truth instead of a stale snapshot.
export async function syncMarketplaceListings() {
    const chars = (gameState.characters || []).filter(c => c.multiplayerId);
    if (chars.length === 0) return;

    const listings = await multiplayer.getMyMarketplaceListings(chars.map(c => c.multiplayerId));
    if (!listings) return; // couldn't reach the server — keep local state as-is

    const byCharacterId = new Map(listings.map(l => [l.character_id, l]));
    let changed = false;

    for (const rosterEntry of chars) {
        const ch = liveCharacter(rosterEntry); // writes must hit the live copy
        const listing = byCharacterId.get(rosterEntry.multiplayerId);

        // A claimed listing is settled: the payout is banked and there is nothing
        // left for the player to do. The server still returns it (it filters on
        // is_active, and claiming only flips completion_claimed), so treating it
        // as live re-created `isMarketplace` with an expiry already in the past —
        // which the expiry check below then "discovered" and announced again on
        // every single load. Settled rows take the clearing path instead.
        const settled = listing && listing.completion_claimed === true;

        if (listing && !settled) {
            const fresh = {
                listedAt: new Date(listing.listed_at).getTime(),
                expiresAt: new Date(listing.expires_at).getTime(),
                durationHours: listing.duration_hours,
                rewardPercentage: listing.reward_percentage,
            };
            const current = ch.marketplaceData;
            const stale = !ch.isMarketplace || !current
                || current.listedAt !== fresh.listedAt
                || current.expiresAt !== fresh.expiresAt
                || current.rewardPercentage !== fresh.rewardPercentage;
            if (stale) {
                ch.isMarketplace = true;
                ch.marketplaceData = fresh;
                if (ch !== rosterEntry) {
                    rosterEntry.isMarketplace = true;
                    rosterEntry.marketplaceData = fresh;
                }
                changed = true;
            }
        } else if (ch.isMarketplace) {
            // Nothing live server-side, so the local flag is stale: either no row
            // at all (withdrawn elsewhere) or a settled one. Cleared quietly on
            // purpose — the expiry announcement belongs to the load that actually
            // did the claiming, not to every load after it.
            ch.isMarketplace = false;
            delete ch.marketplaceData;
            if (ch !== rosterEntry) {
                rosterEntry.isMarketplace = false;
                delete rosterEntry.marketplaceData;
            }
            changed = true;
        }
    }

    if (changed) {
        saveGameState();
        displayCharacter();
    }
}

// Completion claims in flight, keyed by multiplayerId. Module-level (not a
// character property) so the transient guard is never serialized into the save.
const pendingCompletionClaims = new Set();

export function checkMarketplaceExpiryAndPayments() {
  // Check for expired marketplace listings
  let hasMarketplaceChars = false;
  const claimPromises = [];
  if (gameState.characters) {
    const now = Date.now();
    gameState.characters.forEach((c) => {
      const ch = liveCharacter(c); // active character's writes must go here

      if (ch.isMarketplace && ch.marketplaceData?.expiresAt && ch.marketplaceData.expiresAt < now) {
        if (ch.multiplayerId) {
          // Server-listed: try to claim the un-hired completion payout before
          // dropping local state. Clear only once the server responds (the
          // claim is idempotent server-side); on a network error, leave the
          // flag so the next tick retries.
          if (!pendingCompletionClaims.has(ch.multiplayerId)) {
            pendingCompletionClaims.add(ch.multiplayerId);
            const target = ch;
            claimPromises.push(
              multiplayer.claimContractCompletion(target.multiplayerId)
                .then(() => {
                  target.isMarketplace = false;
                  delete target.marketplaceData;
                  addNotification(`${target.name}'s marketplace listing expired`, "info");
                  saveGameState();
                  displayCharacter();
                })
                .catch((err) => console.error("Error claiming contract completion:", err))
                .finally(() => pendingCompletionClaims.delete(target.multiplayerId))
            );
          }
        } else {
          // Local-only listing (no server row to claim against).
          ch.isMarketplace = false;
          delete ch.marketplaceData;
          addNotification(`${ch.name}'s marketplace listing expired`, "info");
        }
      } else if (ch.isMarketplace) {
        hasMarketplaceChars = true;
      }
      saveGameState();
      displayCharacter(); //refresh display for time remaining
    });
  }

  // Check for pending contractor payments
  // Only check for payments if we have marketplace characters that could earn
  if (hasMarketplaceChars && typeof checkAndApplyPendingPayments !== "undefined") {
    checkAndApplyPendingPayments().catch((err) => console.error("Error checking payments:", err));
  }

  // A resolved completion claim inserts a pending payment server-side; collect
  // it once the claims settle so the payout lands in the same visit.
  if (claimPromises.length > 0 && typeof checkAndApplyPendingPayments !== "undefined") {
    Promise.allSettled(claimPromises).then(() => {
      checkAndApplyPendingPayments().catch((err) => console.error("Error checking payments:", err));
    });
  }
}